import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/UI/card';
import { Button } from '@/components/UI/button';
import { Download, Loader2, FileText, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/UI/use-toast';
import { Alert, AlertDescription } from '@/components/UI/alert';
import { loamsApi } from '@/services/api'; // Import loamsApi to access the new API methods
import certificateService from '@/services/certificateService';
// Import the logo as base64 to ensure it's embedded in the PDF
import phoenicianLogoSrc from '@/assets/ZZZZ.png';
import topLogoSrc from '@/assets/PhoenicianLogo.png';
import signImageSrc from '@/assets/Sign.png';

const CompletionCertificate = ({ 
  cluster, 
  agreementRef, 
  month, 
  serviceType: propServiceType, // Rename to clarify it's from props
  previewOnly = false,
  onGenerated = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [certificateData, setCertificateData] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dbServiceType, setDbServiceType] = useState('');
  const [fetchingServiceType, setFetchingServiceType] = useState(false);
  const certificateRef = useRef(null);
  const { toast } = useToast();
  
  // Parse date from month (YYYY-MM format)
  const formatDate = (monthStr) => {
    if (!monthStr) return '';
    
    const [year, monthNum] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
    const monthName = date.toLocaleString('default', { month: 'long' }).toUpperCase();
    return `${monthName} ${year}`;
  };
  
  // Reset state when inputs change
  useEffect(() => {
    setCertificateData(null);
    setError(null);
  }, [cluster, agreementRef, month, propServiceType]);
  
  // Fetch service type from database when cluster and agreement change
  useEffect(() => {
    if (cluster && agreementRef) {
      const fetchServiceType = async () => {
        setFetchingServiceType(true);
        try {
          console.log('[CERT] Fetching service type from database for', { cluster, agreementRef });
          // Call the getAgreementDetails API method we created
          const response = await loamsApi.getAgreementDetails(cluster, agreementRef);
          
          if (response.success && response.data) {
            // Use the formatted service type from the API
            console.log('[CERT] Retrieved service type:', response.data.formatted_service_type);
            setDbServiceType(response.data.formatted_service_type || response.data.service_type);
          } else {
            console.warn('[CERT] No service type found in database, using provided type');
          }
        } catch (error) {
          console.error('[CERT] Error fetching service type:', error);
          // If API fails, we'll fall back to the props service type
        } finally {
          setFetchingServiceType(false);
        }
      };
      
      fetchServiceType();
    }
  }, [cluster, agreementRef]);
  
  useEffect(() => {
    if (previewOnly && cluster && agreementRef && month) {
      // For preview mode, just create local data without PDF generation
      setTimeout(() => {
        // Use database service type if available, fall back to props
        const finalServiceType = dbServiceType || propServiceType;
        const isSwimming = finalServiceType?.toUpperCase()?.includes('SWIMMING') || 
                           finalServiceType?.toUpperCase()?.includes('POOL');
        
        // Get formatted service type
        const formattedServiceType = isSwimming 
          ? 'SWIMMING POOL CLEANING AND MAINTENANCE' 
          : 'LANDSCAPING AND IRRIGATION MAINTENANCE';
        
        setCertificateData({
          cluster,
          agreementRef,
          month: formatDate(month),
          serviceType: finalServiceType,
          isSwimming,
          formattedServiceType, // Store formatted version
          trn: '100058522200003',
        });
      }, 0);
    }
  }, [cluster, agreementRef, month, propServiceType, dbServiceType, previewOnly]);

  const generateCertificate = async () => {
    if (!cluster || !agreementRef || !month) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required fields"
      });
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Prepare data for certificate generation
      const formattedMonth = formatDate(month);
      
      // Use database service type if available, fall back to props
      const finalServiceType = dbServiceType || propServiceType;
      
      // Determine if it's swimming pool or landscaping service
      const isSwimming = finalServiceType?.toUpperCase()?.includes('SWIMMING') || 
                         finalServiceType?.toUpperCase()?.includes('POOL');
      
      // Get formatted service type
      const formattedServiceType = isSwimming 
        ? 'SWIMMING POOL CLEANING AND MAINTENANCE' 
        : 'LANDSCAPING AND IRRIGATION MAINTENANCE';
      
      // Set certificate data for display
      const certData = {
        cluster,
        agreementRef,
        month: formattedMonth,
        serviceType: finalServiceType,
        isSwimming,
        formattedServiceType, // Store formatted version
        trn: '100058522200003',
      };
      
      setCertificateData(certData);
      
      // Generate PDF directly from the content
      setTimeout(async () => {
        try {
          if (certificateRef.current) {
            // Import html2canvas and jsPDF
            const html2canvas = (await import('html2canvas')).default;
            const { jsPDF } = await import('jspdf');
            
            // Preload both logo images to ensure they're properly embedded
            const footerLogoImg = new Image();
            footerLogoImg.crossOrigin = 'Anonymous';
            
            const headerLogoImg = new Image();
            headerLogoImg.crossOrigin = 'Anonymous';
            
            // Create promises to wait for the images to load
            const loadFooterLogoPromise = new Promise((resolve) => {
              footerLogoImg.onload = () => {
                // Convert to base64
                const canvas = document.createElement('canvas');
                canvas.width = footerLogoImg.width;
                canvas.height = footerLogoImg.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(footerLogoImg, 0, 0);
                
                // Update image in the DOM with base64 version
                const footerLogo = certificateRef.current.querySelector('.company-logos img');
                if (footerLogo) {
                  footerLogo.src = canvas.toDataURL('image/png');
                }
                
                resolve();
              };
              footerLogoImg.onerror = (e) => {
                console.error('Footer logo failed to load', e);
                resolve(); // Continue even if image fails to load
              };
              footerLogoImg.src = phoenicianLogoSrc;
            });
            
            const loadHeaderLogoPromise = new Promise((resolve) => {
              headerLogoImg.onload = () => {
                // Convert to base64
                const canvas = document.createElement('canvas');
                canvas.width = headerLogoImg.width;
                canvas.height = headerLogoImg.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(headerLogoImg, 0, 0);
                
                // Update image in the DOM with base64 version
                const headerLogo = certificateRef.current.querySelector('.flex.flex-col.items-end img');
                if (headerLogo) {
                  headerLogo.src = canvas.toDataURL('image/png');
                }
                
                resolve();
              };
              headerLogoImg.onerror = (e) => {
                console.error('Header logo failed to load', e);
                resolve(); // Continue even if image fails to load
              };
              headerLogoImg.src = topLogoSrc;
            });
            
            // Wait for both logos to load and be converted to base64
            await Promise.all([loadFooterLogoPromise, loadHeaderLogoPromise]);
            
            // Add a small delay to ensure DOM updates with base64 logos
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Convert the DOM to canvas with improved settings
            const canvas = await html2canvas(certificateRef.current, {
              scale: 2.5, // Higher scale for better quality
              useCORS: true,
              allowTaint: true,
              logging: false, // Disable logging for production
              backgroundColor: '#FFFFFF',
              height: certificateRef.current.scrollHeight,
              windowHeight: certificateRef.current.scrollHeight,
              scrollY: -window.scrollY
            });
            
            // Create PDF with A4 dimensions (210x297mm)
            const pdf = new jsPDF({
              orientation: 'portrait',
              unit: 'mm',
              format: 'a4',
              compress: true
            });
            
            // Add content to PDF - size adjusted to fit A4 properly
            const imgWidth = 210; // A4 width
            const pageHeight = 297; // A4 height
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            
            // Add the image to fit properly on one page
            pdf.addImage(
              canvas.toDataURL('image/jpeg', 1.0), 
              'JPEG', 
              0, // x position
              0, // y position
              imgWidth, 
              imgHeight < pageHeight ? imgHeight : pageHeight, // ensure it doesn't exceed page height
              '', 
              'FAST'
            );
            
            // Make sure PDF is only one page
            if (pdf.getNumberOfPages() > 1) {
              for (let i = pdf.getNumberOfPages(); i > 1; i--) {
                pdf.deletePage(i);
              }
            }
            
            // Generate download URL
            const pdfBlob = pdf.output('blob');
            const url = URL.createObjectURL(pdfBlob);
            
            // Create filename
            const monthParts = formattedMonth.split(' ');
            const refPrefix = isSwimming ? "CC" : "PO"; // Use CC for swimming, PO for landscaping
            const filename = `${refPrefix}_${agreementRef}_${monthParts[0]}_${monthParts[1]}.pdf`;
            
            // Update certificateData with URL and filename
            setCertificateData(prev => ({
              ...prev,
              url,
              filename
            }));
            
            // Record the generation with certificateService
            try {
              await certificateService.recordGeneration({
                cluster,
                agreementRef,
                month,
                serviceType: finalServiceType
              });
            } catch (recordError) {
              console.warn('Failed to record certificate generation:', recordError);
              // Continue anyway
            }
            
            // Build the full response
            const response = {
              success: true,
              url,
              filename,
              certificate: certData
            };
            
            onGenerated(response);
            
            toast({
              title: "Success",
              description: "Certificate generated successfully"
            });
          }
        } catch (err) {
          console.error("PDF generation error details:", err);
          setError(err.message || "Failed to generate PDF");
          toast({
            variant: "destructive",
            title: "PDF Generation Failed",
            description: err.message || "Failed to generate PDF"
          });
        } finally {
          setLoading(false);
        }
      }, 500); // Give DOM time to update
      
    } catch (error) {
      console.error("Certificate generation error:", error);
      setError(error.message || "Failed to generate certificate");
      toast({
        variant: "destructive",
        title: "Generation Failed",
        description: error.message || "Failed to generate certificate"
      });
      setLoading(false);
    }
  };
  
  const downloadCertificate = async () => {
    if (!certificateData?.url) {
      // Generate PDF on-the-fly if not already generated
      generateCertificate();
      return;
    }
    
    // Use the enhanced download function from certificateService
    setDownloadLoading(true);
    try {
      // This will use our updated function that adds Dubai time and user info
      await certificateService.downloadCertificate(
        certificateData.url, 
        certificateData.filename || `${certificateData.cluster}_${certificateData.agreementRef}_${certificateData.month.replace(' ', '_')}.pdf`
      );
      
      toast({
        title: "Success",
        description: "Certificate downloaded successfully with download information"
      });
    } catch (error) {
      console.error("Download error:", error);
      toast({
        variant: "destructive",
        title: "Download Failed",
        description: error.message || "Failed to download certificate"
      });
    } finally {
      setDownloadLoading(false);
    }
  };
  
  // Service description based on type
  const getServiceDescription = () => {
    if (!certificateData) return '';
    
    const monthParts = certificateData.month.split(' ');
    const monthName = monthParts[0];
    const year = monthParts[1];
    
    if (certificateData.isSwimming) {
      return `${certificateData.cluster} Swimming Pools weekly seven days cleaning, vaccuming and chemical dosing works completed month of ${monthName}-${year}.`;
    } else {
      return `${certificateData.cluster} Landscaping maintenance services work completed month of ${monthName}-${year}.`;
    }
  };
  
  // Format current date for the certificate
  const getTodayDate = () => {
    const today = new Date();
    return `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
  };
  
  return (
    <div className="w-full max-w-3xl mx-auto">
      {!previewOnly && (
        <div className="mb-4">
          <Button 
            onClick={generateCertificate} 
            disabled={loading || !cluster || !agreementRef || !month || fetchingServiceType}
            className="w-full"
            style={{ backgroundColor: '#A2732E' }} // PHOENICIAN_GOLD
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...
              </>
            ) : fetchingServiceType ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Fetching service type...
              </>
            ) : (
              <>
                <FileText className="mr-2 h-4 w-4" /> Generate Certificate
              </>
            )}
          </Button>
        </div>
      )}
      
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {(certificateData || previewOnly) && (
        <Card className="bg-white p-4 print:shadow-none">
          <CardContent className="p-0">
            {/* Download controls (hidden when printing) */}
            <div className="mb-4 flex justify-end print:hidden">
              <Button 
                variant="outline"
                onClick={downloadCertificate}
                disabled={downloadLoading}
              >
                {downloadLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Download
              </Button>
            </div>
            
            {/* Certificate Content - matching exact layout */}
            <div 
              ref={certificateRef} 
              className="certificate-content bg-white" 
              style={{ 
                width: '210mm',
                minHeight: '297mm', // Changed to minHeight
                position: 'relative',
                padding: '20mm',
                fontFamily: 'Arial, sans-serif',
                boxSizing: 'border-box',
                margin: '0 auto'
              }}
            >
              <div className="flex justify-between items-center mb-6">
                <div className="text-center flex-1">
                  <p className="text-sm font-bold">TRN NO.: {certificateData?.trn}</p>
                </div>
                <div className="flex flex-col items-end">
                  <img 
                    src={topLogoSrc} 
                    alt="Phoenician Logo" 
                    className="h-16" 
                    onError={(e) => {
                      console.error('Top logo failed to load');
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>

              {/* FIXED AREA - Now using formattedServiceType from database */}
              <div className="flex justify-between mb-6">
                <div>
                  <p className="text-sm font-bold mb-1">TO,</p>
                  <p className="font-bold">M/S LOAMS LLC,</p>
                  <p className="font-bold">{certificateData?.cluster}</p>
                  <p className="font-bold">{certificateData?.formattedServiceType}</p>
                </div>
                <div>
                  <p className="text-sm font-bold">DATE: {certificateData?.month}</p>
                </div>
              </div>
              
              {/* COMPLETION CERTIFICATE text - bold */}
              <div className="text-center mb-6">
                <h2 className="text-lg font-bold">COMPLETION CERTIFICATE</h2>
              </div>
              
              {/* Certificate Reference Table */}
              <table className="w-full border-collapse mb-6">
                <tbody>
                  <tr>
                    <td className="border border-gray-400 p-2 text-center w-1/3">
                      <p className="text-sm font-bold">{certificateData?.isSwimming ? 'CC REF:' : 'PO REF:'}</p>
                    </td>
                    <td className="border border-gray-400 p-2 text-center w-2/3">
                      <p className="text-sm font-bold">{agreementRef}</p>
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 p-2 text-center">
                      <p className="text-sm font-bold">{certificateData?.cluster}</p>
                    </td>
                    <td className="border border-gray-400 p-2 text-center">
                      <p className="text-sm font-bold">{certificateData?.formattedServiceType}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
              
              {/* Completion text */}
              <div className="mb-6">
                <p className="text-sm font-bold">
                  We would like to inform you that the job awarded to us has been completed according to
                  contract terms and schedule
                </p>
              </div>
              
              {/* Main table with exact height as shown in images */}
              <table className="w-full border-collapse mb-6">
                <tbody>
                  <tr>
                    <td className="border border-gray-400 p-2" style={{width: '50%', textAlign: 'center'}}>
                      <p className="text-sm font-bold">Description</p>
                    </td>
                    <td className="border border-gray-400 p-2" style={{width: '50%', textAlign: 'center'}}>
                      <p className="text-sm font-bold">Completion Date</p>
                    </td>
                  </tr>
                  <tr height="80">
                    <td className="border border-gray-400 p-3 align-top">
                      <div>
                        <p className="text-sm">
                          <span className="font-bold">{certificateData?.cluster}</span>{' '}
                          {certificateData?.isSwimming 
                            ? `Swimming Pools weekly seven days cleaning, vaccuming and chemical dosing works completed month of ${certificateData?.month?.split(' ')[0]}-${certificateData?.month?.split(' ')[1]}.`
                            : `Landscaping maintenance services work completed month of ${certificateData?.month?.split(' ')[0]}-${certificateData?.month?.split(' ')[1]}.`
                          }
                        </p>
                      </div>
                      <div className="mt-6 mb-2">
                        <p className="text-sm font-bold">Work Completed</p>
                      </div>
                    </td>
                    <td className="border border-gray-400 p-3 align-middle text-center">
                      <p className="text-sm font-bold">{certificateData?.month}</p>
                    </td>
                  </tr>
                  <tr height="110">
                    <td className="border border-gray-400 p-3 align-top"> 
                      <p className="text-sm font-bold">Certified By: LOAMS LLC</p>
                    </td>
                    <td className="border border-gray-400 p-3 align-top">
                      <p className="text-sm font-bold">Name:</p>
                      <p className="text-sm font-bold">Signature:</p>
                    </td>
                  </tr>
                </tbody>
              </table>
              
              {/* Certification text */}
              <div className="mb-6">
                <p className="text-sm font-bold">
                  We certify that the job has been completed as per schedule and based on the scope of work
                  mentioned there in
                </p>
              </div>
              
              {/* Contact information */}
              <div className="mb-10">
                <p className="text-sm font-bold">
                  Should required any further information, please contact us on, 054-9918014
                </p>
              </div>
              
              {/* Phoenician signature */}
              {/* Phoenician signature - Updated with correct order */}
<div className="mt-10 text-right">
  <p className="text-sm font-bold mb-2">For,</p>
  <p className="text-sm font-bold mb-4">Phoenician Technical Services LLC</p>
  <img 
    src={signImageSrc} 
    alt="Signature" 
    style={{ 
      height: '20mm',
      objectFit: 'contain',
      marginLeft: 'auto' // Ensures the image stays aligned to the right
    }} 
  />
</div>
              
              {/* Company footer section - Using non-absolute positioning for better rendering */}
              <div className="certificate-footer" style={{ 
                marginTop: '30mm',
                paddingTop: '10mm',
                borderTop: '0.5px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                width: '100%',
              }}>
                {/* Company address - left side - INCREASED FONT SIZE */}
                <div className="company-address" style={{ 
                  fontSize: '9px', // Increased from 8px to 9px
                  color: '#A2732E', 
                  textAlign: 'left',
                  fontWeight: 'bold',
                  lineHeight: '1.5' // Slightly increased line height
                }}>
                  PHOENICIAN TECHNICAL SERVICES LLC<br />
                  OFFICE 399 CENTRAL PLAZA, DIP-1, DUBAI, UAE<br />
                  P.O.BOX: 377872 - DUBAI<br />
                  T: 048829484<br />
                  W: WWW.PHOENICIAN-UAE.COM
                </div>
                
                {/* Company logos - right side */}
                <div className="company-logos">
                  <img 
                    src={phoenicianLogoSrc} 
                    alt="Company Logos" 
                    onError={(e) => {
                      console.error('Logo failed to load');
                      e.target.style.display = 'none';
                    }}
                    style={{ 
                      height: '15mm',
                      objectFit: 'contain'
                    }} 
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompletionCertificate;